import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import isNil from 'ramda/es/isNil'
import findIndex from 'ramda/es/findIndex'
import find from 'ramda/es/find'
import sortBy from 'ramda/es/sortBy'
import prop from 'ramda/es/prop'
import curry from 'ramda/es/curry'
import { actionTypes } from 'Shared'
import { persistPiniaSlice, persistPiniaSliceDebounced, loadPiniaSlice, slimPersistedAnswer } from '../persist'
import { scheduleTestSessionPatch } from '../testSession'

const findByQuestionID = curry(
    (questionID, answer) => questionID === answer.question.id
)

export const useAnswersStore = defineStore('answers', () => {
    const userAnswers = ref([])
    const selectedUserAnswerIndex = ref(-1)

    const persist = ({ debounced = false } = {}) => {
        const payload = {
            userAnswers: userAnswers.value.map(slimPersistedAnswer),
            selectedUserAnswerIndex: selectedUserAnswerIndex.value,
        }
        if (debounced) persistPiniaSliceDebounced('answersStore', payload)
        else persistPiniaSlice('answersStore', payload)
        scheduleTestSessionPatch({ userAnswers: userAnswers.value })
    }

    const userAnswer = (questionID) =>
        userAnswers.value.find((item) => item.question.id == questionID)

    const existsAnswerFor = (questionID) =>
        !isNil(userAnswers.value.find((item) => item.question.id == questionID))

    const findIndexOfId = (questionID) =>
        userAnswers.value.findIndex((ua) => ua.question.id == questionID)

    const expectedDurationForAnsweredQuestionsInSeconds = computed(() =>
        userAnswers.value.reduce(
            (acc, answer) => (acc += answer.expectedDurationInSeconds),
            0
        )
    )

    const resetAnswers = () => {
        userAnswers.value = []
        selectedUserAnswerIndex.value = -1
        persist()
    }

    const setSelectedUserAnswerIndex = (newIndex) => {
        if (newIndex > -1 && newIndex < userAnswers.value.length) {
            selectedUserAnswerIndex.value = newIndex
            persist()
        }
    }

    const incSelectedUserAnswerIndex = () => {
        setSelectedUserAnswerIndex(selectedUserAnswerIndex.value + 1)
    }

    const decSelectedUserAnswerIndex = () => {
        setSelectedUserAnswerIndex(selectedUserAnswerIndex.value - 1)
    }

    const addToUserAnswers = (answer) => {
        userAnswers.value.push(answer)
        persist()
    }

    const modifyUserAnswer = ({ key, userInput, action, question }) => {
        const predicate = (a) => a.question.id === question.id
        const index = findIndex(predicate)(userAnswers.value)

        if (index < 0) {
            console.warn('Specified answer does not exist -> No modification!')
            return
        }

        const oldAnswer = userAnswers.value[index]

        if (key === 'audio') {
            oldAnswer.audioUuid = userInput
        } else {
            const userInputIndex = oldAnswer.userInput.findIndex(
                (item) => item.key === key
            )
            const userInputExists = userInputIndex > -1

            if (!userInputExists && action !== actionTypes.remove)
                oldAnswer.userInput.push({ key, value: userInput })

            if (action === actionTypes.remove && userInputExists)
                oldAnswer.userInput.splice(userInputIndex, 1)
            if (action === actionTypes.replace && userInputExists)
                oldAnswer.userInput[userInputIndex].value = userInput
        }

        oldAnswer.ignored = false
        persist({ debounced: true })
    }

    const sortUserAnswersByQuestionIndex = () => {
        userAnswers.value = userAnswers.value.sort(
            (a1, a2) => a1.questionIndex - a2.questionIndex
        )
        persist()
    }

    const incDurationOfQuestion = ({ questionID, duration }) => {
        const predicate = (a) => a.question.id === questionID
        const index = findIndex(predicate)(userAnswers.value)

        if (index !== -1 && duration > 0) {
            userAnswers.value[index].actualDurationInSeconds += duration
            persist({ debounced: true })
        }
    }

    const markQuestionInAnswer = (id) => {
        find(findByQuestionID(id), userAnswers.value).question.marked = true
        persist()
    }

    const unmarkQuestionInAnswer = (id) => {
        find(findByQuestionID(id), userAnswers.value).question.marked = false
        persist()
    }

    const setToIgnored = (questionID) => {
        const answer = userAnswers.value.find((item) => item.question && item.question.id == questionID)
        if (!answer) return
        answer.ignored = true
        answer.userInput = []
        persist()
    }

    const setUserAnswersTo = (answers) => {
        const sortByIdx = sortBy(prop('question_index'))
        userAnswers.value = sortByIdx(answers)
        selectedUserAnswerIndex.value = 0
        persist()
    }

    const hydrateUserAnswers = (answers) => {
        if (Array.isArray(answers)) userAnswers.value = answers
    }

    const loadFromLocalStorage = () => {
        const slice = loadPiniaSlice('answersStore')
        if (!slice) return
        if (Array.isArray(slice.userAnswers)) {
            userAnswers.value = slice.userAnswers
        }
        if (typeof slice.selectedUserAnswerIndex === 'number') {
            selectedUserAnswerIndex.value = slice.selectedUserAnswerIndex
        }
    }

    loadFromLocalStorage()

    return {
        userAnswers,
        selectedUserAnswerIndex,

        userAnswer,
        existsAnswerFor,
        findIndexOfId,
        expectedDurationForAnsweredQuestionsInSeconds,

        setSelectedUserAnswerIndex,
        incSelectedUserAnswerIndex,
        decSelectedUserAnswerIndex,
        sortUserAnswersByQuestionIndex,
        addToUserAnswers,
        modifyUserAnswer,
        markQuestionInAnswer,
        unmarkQuestionInAnswer,
        setToIgnored,
        resetAnswers,
        setUserAnswersTo,
        incDurationOfQuestion,
        hydrateUserAnswers,
    }
})
